import { CalendarIcon, Repeat } from "lucide-react"

import {
  AlertDialog,
  AlertDialogBody,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog"
import { cn } from "@/lib/utils"

import type { SaveConfirmPending } from "./usePlannedRoutePersistence"

/** This day only = route instances; all future routes = planned templates. */
export function SaveScopeDialog({
  pending,
  onDismiss,
  onThisDayOnly,
  onAllFuture,
}: {
  pending: SaveConfirmPending | null
  onDismiss: () => void
  onThisDayOnly: () => void
  onAllFuture: () => void
}) {
  return (
    <AlertDialog
      open={!!pending}
      onOpenChange={(open) => !open && onDismiss()}
    >
      <AlertDialogContent size="form">
        <AlertDialogHeader>
          <AlertDialogTitle>How should we apply your changes?</AlertDialogTitle>
          <AlertDialogDescription>
            {pending?.summary.length === 1
              ? "1 route has unsaved changes."
              : `${pending?.summary.length} routes have unsaved changes.`}
          </AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogBody>
          {pending?.summary && pending.summary.length > 0 && (
            <div className="rounded-lg bg-palette-jungleGreen000 border border-palette-jungleGreen100 px-3 py-2.5">
              <p className="text-xs font-medium text-palette-jungleGreen500 uppercase tracking-wide mb-1.5">
                Changes
              </p>
              <ul className="space-y-1 text-sm">
                {pending.summary.map((item, i) => (
                  <li
                    key={pending.routes[i]?.id ?? i}
                    className="text-palette-jungleGreen700"
                  >
                    <span className="font-medium text-palette-jungleGreen900">
                      {item.routeCode}
                    </span>
                    <span className="text-palette-jungleGreen500">
                      {" "}
                      — {item.changes.join(", ")}
                    </span>
                  </li>
                ))}
              </ul>
            </div>
          )}
          <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
            <button
              type="button"
              onClick={onThisDayOnly}
              className={cn(
                "flex flex-col items-start gap-2 rounded-lg border-2 p-4 text-left transition-colors",
                "border-palette-jungleGreen200 bg-white hover:border-blue-300 hover:bg-blue-50/50 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2",
              )}
            >
              <div className="flex items-center gap-2">
                <div className="rounded-md bg-blue-100 p-1.5">
                  <CalendarIcon className="h-4 w-4 text-blue-600" />
                </div>
                <span className="font-semibold text-palette-jungleGreen900">This day only</span>
              </div>
              <p className="text-xs text-palette-jungleGreen500 leading-snug">
                Apply changes only to the selected date. Other dates are unchanged.
              </p>
            </button>
            <button
              type="button"
              onClick={onAllFuture}
              className={cn(
                "flex flex-col items-start gap-2 rounded-lg border-2 p-4 text-left transition-colors",
                "border-palette-jungleGreen200 bg-white hover:border-amber-300 hover:bg-amber-50/50 focus:outline-none focus:ring-2 focus:ring-amber-500 focus:ring-offset-2",
              )}
            >
              <div className="flex items-center gap-2">
                <div className="rounded-md bg-amber-100 p-1.5">
                  <Repeat className="h-4 w-4 text-amber-600" />
                </div>
                <span className="font-semibold text-palette-jungleGreen900">
                  All future routes
                </span>
              </div>
              <p className="text-xs text-palette-jungleGreen500 leading-snug">
                Update the planned route template. Changes apply to this and all future dates.
              </p>
              <p className="text-xs text-amber-500 leading-snug">
                Overwrites any edited future instances.
              </p>
            </button>
          </div>
        </AlertDialogBody>
        <AlertDialogFooter>
          <AlertDialogCancel onClick={onDismiss}>
            Cancel
          </AlertDialogCancel>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  )
}
